import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMudancaEvidencias } from '@/hooks/useMudancaEvidencias';
import { Upload, Download, Trash2, FileText, Image as ImageIcon, FileSpreadsheet } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface MudancaEvidenciasProps {
  mudancaId: string;
}

const MudancaEvidencias = ({ mudancaId }: MudancaEvidenciasProps) => {
  const { evidencias, uploadEvidencia, deleteEvidencia } = useMudancaEvidencias(mudancaId);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [tipo, setTipo] = useState<string>('documentacao');
  const [etapaWorkflow, setEtapaWorkflow] = useState<string | undefined>(undefined);
  const [filtroEtapa, setFiltroEtapa] = useState<string>('todas');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadEvidencia.mutate({
        mudancaId,
        file: selectedFile,
        tipo,
        etapaWorkflow,
      });
      setSelectedFile(null);
      setEtapaWorkflow(undefined);
    }
  };

  const handleDelete = (evidenciaId: string) => {
    if (confirm('Deseja realmente deletar esta evidência?')) {
      deleteEvidencia.mutate(evidenciaId);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType?.startsWith('image/')) return <ImageIcon className="w-8 h-8" />;
    if (mimeType?.includes('pdf')) return <FileText className="w-8 h-8" />;
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return <FileSpreadsheet className="w-8 h-8" />;
    return <FileText className="w-8 h-8" />;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const evidenciasFiltradas = filtroEtapa === 'todas' 
    ? evidencias 
    : evidencias.filter(e => e.etapa_workflow === filtroEtapa);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Evidências da Mudança
          </span>
          <Badge variant="secondary">{evidencias.length} arquivo(s)</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Área de Upload */}
        <div className="border-2 border-dashed rounded-lg p-6 space-y-4">
          <h4 className="font-medium">Upload de Nova Evidência</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="tipo">Tipo de Evidência</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="documentacao">Documentação</SelectItem>
                  <SelectItem value="screenshot">Screenshot</SelectItem>
                  <SelectItem value="relatorio">Relatório</SelectItem>
                  <SelectItem value="log">Log</SelectItem>
                  <SelectItem value="aprovacao">Aprovação</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="etapa">Etapa do Workflow (Opcional)</Label>
              <Select value={etapaWorkflow} onValueChange={(value) => setEtapaWorkflow(value || undefined)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="criacao">Criação</SelectItem>
                  <SelectItem value="avaliacao">Avaliação</SelectItem>
                  <SelectItem value="aprovacao">Aprovação</SelectItem>
                  <SelectItem value="execucao">Execução</SelectItem>
                  <SelectItem value="validacao">Validação</SelectItem>
                  <SelectItem value="encerramento">Encerramento</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="file">Arquivo</Label>
              <Input
                id="file"
                type="file"
                onChange={handleFileSelect}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.log"
              />
            </div>
            <Button 
              onClick={handleUpload} 
              disabled={!selectedFile || uploadEvidencia.isPending}
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadEvidencia.isPending ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>

          {selectedFile && (
            <p className="text-sm text-muted-foreground">
              Arquivo selecionado: {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </p>
          )}
        </div>

        {/* Filtro */}
        <div className="flex items-center gap-2">
          <Label>Filtrar por etapa:</Label>
          <Select value={filtroEtapa} onValueChange={setFiltroEtapa}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              <SelectItem value="criacao">Criação</SelectItem>
              <SelectItem value="avaliacao">Avaliação</SelectItem>
              <SelectItem value="aprovacao">Aprovação</SelectItem>
              <SelectItem value="execucao">Execução</SelectItem>
              <SelectItem value="validacao">Validação</SelectItem>
              <SelectItem value="encerramento">Encerramento</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Grid de Evidências */}
        <div className="grid grid-cols-3 gap-4">
          {evidenciasFiltradas.map((evidencia) => (
            <Card key={evidencia.id} className="overflow-hidden">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-center p-4 bg-muted rounded-lg">
                  {getFileIcon(evidencia.mime_type || '')}
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium truncate" title={evidencia.nome_arquivo}>
                    {evidencia.nome_arquivo}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(evidencia.tamanho_bytes)}
                  </p>
                </div>

                {evidencia.etapa_workflow && (
                  <Badge variant="outline" className="text-xs">
                    {evidencia.etapa_workflow}
                  </Badge>
                )}

                <Badge variant="secondary" className="text-xs">
                  {evidencia.tipo}
                </Badge>

                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => window.open(evidencia.url_arquivo, '_blank')}
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(evidencia.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                  {new Date(evidencia.criado_em).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {evidenciasFiltradas.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            Nenhuma evidência encontrada
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MudancaEvidencias;
